import { useCallback, useState } from 'react'
import { useMutation } from '@tanstack/react-query'
import { SubmitHandler, useFormContext } from 'react-hook-form'
import APIService from 'api/APIService'
import { AuthFormValue } from 'pages/auth/type'
import Button from 'core/components/buttons/Button'
import AppleLogin from 'react-apple-login'
import AppleLogo from 'assets/images/apple_logo_black.png'
import GoogleLogo from 'assets/images/google_logo.png'
import { useUserProfile } from 'core/hooks/data/useDataHook'
import AnalyticsHelper from 'utils/AnalyticsHelper'
import { useGoogleLogin } from '@react-oauth/google'
import useNavigation from 'core/hooks/navigation/useNavigation'
import SmartField from 'core/components/form/SmartField'
import Input from 'core/components/inputs/Input'
import StartLandingBg from 'assets/images/start-page-bg.png'
import ComponentErrorBoundary from 'core/widgets/ComponentErrorBoundary'
import Icon from 'core/components/icons/Icon'
import {
  DashboardUrl,
  LoginUrl,
  SignUpAboutYouUrl,
  SignupUrl,
  StartUrl
} from 'constants/RouteUrls'
import FeatureIcon from 'core/components/icons/FeatureIcon'
import useAuth from 'core/hooks/auth/useAuth'
import {
  LINK_EVERLANCE_PRIVACY,
  LINK_EVERLANCE_TERMS_OF_SERVICE
} from 'constants/Links'
import useNotifications from 'core/hooks/ui/useNotifications'

const valueProps = [
  {
    title: 'Get started for free',
    description: 'Easy set up with no credit card required'
  },
  {
    title: 'Automatic trip & expense tracking',
    description:
      'GPS satellites ensure accurate trip tracking even without cell phone service.  Integrate your bank or credit card for easy expense management.'
  },
  {
    title: 'IRS compliant reports',
    description: 'Export a complete record of your trips and expenses.'
  }
]

const AuthStartPage = () => {
  const { push, search } = useNavigation()
  const searchParams = new URLSearchParams(search)
  const group_company_name = searchParams.get('group_company_name')
  const group_image_url = searchParams.get('group_image_url')
  const { setData } = useUserProfile()
  const [externalLoginError, setExternalLoginError] = useState(false)
  const { setUserId } = useAuth()
  const notification = useNotifications()
  const { control, handleSubmit, getValues } = useFormContext<AuthFormValue>()
  const {
    status: loginStatus,
    isError,
    mutate
  } = useMutation({
    mutationKey: ['authStart'],
    mutationFn: APIService.users.authAction,
    onSuccess: (data) => {
      if (data.next_action === 'saml_login') {
        const redirectLink = `${
          import.meta.env.VITE_APP_API_BASE_URL
        }saml_login?email=${encodeURIComponent(getValues('email'))}`.replace(
          'v6/',
          ''
        )
        window.location.assign(redirectLink)
      }
      if (data.next_action === 'sign_in') {
        push(`${LoginUrl}${search}`, { search })
        return
      }
      if (data.next_action === 'sign_up') {
        push(`${SignupUrl}${search}`, { search })
        return
      }
    }
  })

  const googleLogin = useGoogleLogin({
    flow: 'auth-code',
    onSuccess: (tokenResponse) => handleSocialLogin(tokenResponse, 'google'),
    onError: () => {
      setExternalLoginError(true)
    }
  })

  const setUserID = useCallback(
    (userID: string, callback: () => void) => {
      setUserId(userID)
      callback()
    },
    [setUserId]
  )

  const {
    isError: isErrorSocial,
    isPending: socialLoginLoading,
    mutate: mutateSocial
  } = useMutation({
    mutationKey: ['authSocial'],
    mutationFn: APIService.users.oauthLogin,
    onSuccess: async (user) => {
      setExternalLoginError(false)
      setData(user)

      if (user.email && user.next_action === 'saml_login') {
        const redirectLink = `${
          import.meta.env.VITE_APP_API_BASE_URL
        }saml_login?email=${user.email}`.replace('v6/', '')
        window.location.assign(redirectLink)
        return
      }

      setUserID(user.token_id.toString(), () => {
        if (user.is_new_user) {
          const provider = user.connected_to_apple
            ? 'apple'
            : user.connected_to_google
              ? 'google'
              : 'email'
          AnalyticsHelper.trackEvent('Sign Up', { provider: provider })
          // Singular registration conversion (REQ-EVC-2253-003 / REQ-EVC-2253-015).
          // Fires once per account created through OAuth, carrying the provider
          // it was created with.
          AnalyticsHelper.trackEvent('sng_complete_registration', {
            sng_attr_registration_method: provider
          })
          push(SignUpAboutYouUrl)
          return
        }
        // Singular login conversion (REQ-EVC-2253-017). A returning user is
        // logged in the moment the OAuth response resolves, so this rides the
        // sign-in rather than the deferred redirect timer below.
        AnalyticsHelper.trackEvent('sng_login', { sng_attr_success: true })
        setTimeout(() => {
          push(DashboardUrl)
        }, 2000)
      })
    },
    onError: () => {
      setTimeout(() => {
        push(StartUrl)
      }, 3000)
    }
  })

  const handleSocialLogin = useCallback(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (response: any, provider: 'apple' | 'google') => {
      if (provider === 'apple') {
        if (response.error) {
          setExternalLoginError(true)
        }
        mutateSocial({
          provider: 'apple',
          id_token: response.authorization.id_token,
          authorization_code: response.authorization.code
        })
      }
      if (provider === 'google') {
        mutateSocial({
          provider: 'google',
          authorization_code: response.code
        })
      }
    },
    [mutateSocial]
  )

  const onSubmit: SubmitHandler<AuthFormValue> = useCallback(
    (formData) => {
      notification.dismiss('session-expired')
      mutate(formData.email)
    },
    [mutate, notification]
  )

  const socialLoginError = isErrorSocial || externalLoginError

  return (
    <div
      data-role="auth-start-container"
      className="h-screen flex justify-around items-center relative"
    >
      <img
        src={StartLandingBg}
        alt="road background with place markers"
        className="absolute z-0 w-screen h-screen object-cover"
      />
      <div className="flex-col items-center hidden md:flex p-4 basis-[500px] z-10">
        <span className="flex items-center mb-10 gap-2">
          <FeatureIcon name="everlance-by-motus-logo" width={280} />
        </span>
        <div>
          {valueProps.map((valueProp) => (
            <div className="flex flex-row mb-5" key={valueProp.title}>
              <div className="mr-4 mt-1">
                <Icon
                  className="text-white p-1 bg-primary rounded-full"
                  size={14}
                  name="check"
                />
              </div>

              <div className="text-xl">
                <strong>{valueProp.title}</strong>
                <p className="text-dark-4">{valueProp.description}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
      <div className="card bg-white md:px-20 md:max-w-xl basis-[500px] z-10">
        <div className="flex flex-col justify-center h-full">
          {group_image_url && (
            <div className="flex justify-center">
              <img
                className="max-h-14"
                alt="group_logo"
                src={group_image_url}
              />
            </div>
          )}
          {group_company_name && (
            <div className="flex justify-center">
              <h3 title="group-name">{group_company_name}</h3>
            </div>
          )}
          <div className="flex justify-center">
            <h3 data-role="title" className="text-2xl mb-6 mt-6">
              What's your email?
            </h3>
          </div>
          <form onSubmit={handleSubmit(onSubmit)}>
            <SmartField
              className="block!"
              controllerProps={{
                control,
                name: 'email',
                rules: {
                  validate: {
                    noblank: (value) =>
                      value.trim() !== '' || 'Email cannot be blank'
                  }
                }
              }}
            >
              <Input
                placeholder="Enter your email"
                className="text-lg mb-2 w-full py-3.5"
              />
            </SmartField>
            <Button
              type="submit"
              status={loginStatus}
              variant="primary"
              className="w-full mt-2 py-3!"
            >
              Continue with Email / SSO
            </Button>
          </form>
          {isError && (
            <p className="text-center">
              An error happened. Please try again later.{' '}
            </p>
          )}
          <div className="text-center my-8 overflow-hidden">
            <hr className="border-dark-3 mt-4" />
            <span className="block w-20 mx-auto bg-white text-dark-3 px-4 mt-[-10px]">
              OR
            </span>
          </div>
          {socialLoginLoading ? (
            <div className="flex items-center justify-center h-24">
              <Icon name="loading" className="text-primary" size={60} />
            </div>
          ) : (
            <div className="flex flex-col gap-4">
              <ComponentErrorBoundary>
                <AppleLogin
                  clientId={`${import.meta.env.VITE_APP_APPLE_CLIENT_ID}`}
                  redirectURI={`${import.meta.env.VITE_APP_AUTH_REDIRECT_URL}`}
                  usePopup={true}
                  callback={(appleResponse) => {
                    handleSocialLogin(appleResponse, 'apple')
                  }}
                  scope="email name"
                  responseMode="query"
                  responseType="id_token"
                  render={(renderProps) => (
                    <Button
                      variant="outline"
                      onClick={(e) => {
                        AnalyticsHelper.trackEvent('Splash Apple Login')
                        renderProps.onClick(e)
                      }}
                      className="flex! flex-row items-center w-full justify-center border-dark-3 text-base font-medium"
                      prefix={
                        <img
                          src={AppleLogo}
                          alt="apple logo"
                          className="inline-block mr-1 h-7"
                        />
                      }
                    >
                      Sign in with Apple
                    </Button>
                  )}
                />
              </ComponentErrorBoundary>

              <Button
                variant="outline"
                onClick={() => {
                  AnalyticsHelper.trackEvent('Splash Google Login')
                  googleLogin()
                }}
                className="flex items-center w-full justify-center border-dark-3 text-base font-medium"
                prefix={
                  <img
                    src={GoogleLogo}
                    alt="google logo"
                    className="inline-block mr-1 w-7"
                  />
                }
              >
                Sign in with Google
              </Button>
            </div>
          )}
          {socialLoginError && (
            <p className="mb-0 mt-2 text-danger text-center">
              An error happened. Please try again later.
            </p>
          )}
          <div className="mt-28">
            <p className="text-sm text-center">
              By signing up, you agree to our{' '}
              <a
                href={LINK_EVERLANCE_TERMS_OF_SERVICE}
                target="_blank"
                rel="noreferrer"
                className="underline text-sm"
              >
                Terms and Conditions
              </a>{' '}
              and acknowledge that you have read our{' '}
              <a
                href={LINK_EVERLANCE_PRIVACY}
                target="_blank"
                rel="noreferrer"
                className="underline text-sm"
              >
                Privacy Policy
              </a>
              .
            </p>
          </div>
        </div>
      </div>
    </div>
  )
}

export default AuthStartPage
